/**
 * Shared client-autocomplete pure model (DOM-free).
 *
 * The substring filter, first-N-when-empty cap, "Add new: <query>" offer, and the
 * critical RENAME edge (a typed name that no longer matches the linked client
 * resets `client_id` to null → create a NEW client) originated in the E02-F02
 * project-detail card (`renderClientAutocomplete`, legacy `project.js`). They now
 * back TWO surfaces — the project detail header AND the E06 Research Intake form —
 * so the rules live here (feature-neutral `shared/`) and both features import from
 * here instead of one feature reaching into the other's folder. `project/` keeps
 * re-exporting these for its existing importers.
 */
import type { ClientOption } from '@eer/shared';

/** How many options the dropdown shows when the query is empty (legacy cap). */
export const CLIENT_AUTOCOMPLETE_EMPTY_LIMIT = 10;

/**
 * Filter the clients cache for the autocomplete dropdown (legacy
 * `renderClientAutocomplete`):
 *   - empty query  → the FIRST 10 clients (the cache is already name-sorted);
 *   - non-empty    → every client whose name CONTAINS the query (case-insensitive
 *     substring), unbounded (the user is narrowing).
 *
 * Never throws; a non-array cache yields `[]`.
 */
export function filterClientOptions(
  clients: readonly ClientOption[] | null | undefined,
  query: string,
): ClientOption[] {
  if (!Array.isArray(clients)) return [];
  const q = query.trim().toLowerCase();
  if (q.length === 0) return clients.slice(0, CLIENT_AUTOCOMPLETE_EMPTY_LIMIT);
  return clients.filter((c) => c.name.toLowerCase().includes(q));
}

/**
 * Whether to offer an "Add new: <query>" row (legacy: shown when the typed name
 * is non-blank AND no client matches it EXACTLY, case-insensitive). Selecting it
 * stages `client_id = null` (create-a-new-client on save).
 */
export function shouldOfferAddNew(
  clients: readonly ClientOption[] | null | undefined,
  query: string,
): boolean {
  const q = query.trim();
  if (q.length === 0) return false;
  const list = Array.isArray(clients) ? clients : [];
  return !list.some((c) => c.name.trim().toLowerCase() === q.toLowerCase());
}

/** The label of the "Add new" row for a typed query. */
export function addNewLabel(query: string): string {
  return `Add new: ${query.trim()}`;
}

/** The result of resolving a typed/selected client name against the cache. */
export interface ClientResolution {
  /**
   * The visible text — the user's RAW typed value, untrimmed (PK-QA-12). This drives
   * the CONTROLLED autocomplete input while typing, so it must preserve every space
   * the user enters (a per-keystroke `.trim()` here made multi-word names impossible
   * to type). Trimming happens at the SAVE boundary (header `diffForm` / intake
   * submit), NOT on this live value.
   */
  readonly client_name: string;
  /**
   * `client_id` to send: a number when the typed name EXACTLY matches a cached
   * client, or `null` to force-create a new client (the "Add new" / rename edge).
   * `null` is the legacy "create new" sentinel — NOT "untouched".
   */
  readonly client_id: number | null;
}

/**
 * Resolve a free-typed client name against the cache to a `{client_name,
 * client_id}` pair (legacy `renderClientAutocomplete` commit + the CRITICAL rename
 * edge):
 *
 *   - the name EXACTLY matches a cached client (case-insensitive, whitespace-
 *     insensitive) → link to that client's id;
 *   - the name does NOT match any cached client (a fresh "Add new", OR the user
 *     RENAMED a previously-linked client so it no longer matches) → `client_id =
 *     null`, which tells the api to CREATE a new client from `client_name`.
 *
 * This is the bug-prone case the review called out: a rename must drop the old
 * link, never silently keep `client_id` pointing at the old client.
 *
 * PK-QA-12: this runs on EVERY keystroke of the CONTROLLED autocomplete input, and
 * the returned `client_name` is fed straight back as the input's value. So the
 * lookup KEY is trimmed (leading/trailing spaces must never block the exact-match
 * link) while the returned `client_name` stays the user's RAW text — otherwise a
 * trailing space typed as the last character would be stripped before the next
 * keystroke and multi-word names could never be typed. The write boundary
 * (`diffForm` for the header, submit for the intake form) does the trimming.
 */
export function resolveClientName(
  clients: readonly ClientOption[] | null | undefined,
  typedName: string,
): ClientResolution {
  const lookupKey = typedName.trim();
  const list = Array.isArray(clients) ? clients : [];
  const match = list.find((c) => c.name.trim().toLowerCase() === lookupKey.toLowerCase());
  return { client_name: typedName, client_id: match ? match.id : null };
}
